import "./Login.css"
import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { auth } from "../firebase"
import { sendEmailVerification, signOut } from "firebase/auth"

function VerifyEmail() {
  const [loaded, setLoaded] = useState(false)
  const [imgLoaded, setImgLoaded] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(50)
  const [resendLoading, setResendLoading] = useState(false)
  const [abortLoading, setAbortLoading] = useState(false)
  const [alert, setAlert] = useState(null)
  const [verified, setVerified] = useState(false)

  const navigate = useNavigate()

  const user = auth.currentUser
  const userEmail = user?.email || "your email"

  const showAlert = (message, type = "info") => {
    setAlert({ message, type })
    setTimeout(() => setAlert(null), 5000)
  }

  const clearAlert = () => setAlert(null)

  useEffect(() => {
    window.scrollTo(0, 0)
    const timer = setTimeout(() => setLoaded(true), 100)
    return () => clearTimeout(timer)
  }, [])

  // Persistent countdown logic
  useEffect(() => {
    if (verified) return

    let storedTime = localStorage.getItem("verifyEmailStartTime")

    if (!storedTime) {
      storedTime = Date.now().toString()
      localStorage.setItem("verifyEmailStartTime", storedTime)
    }

    const updateTimer = () => {
      const diff = Math.floor((Date.now() - parseInt(storedTime)) / 1000)
      const remaining = 50 - diff
      setSecondsLeft(remaining > 0 ? remaining : 0)
    }

    updateTimer()

    const interval = setInterval(updateTimer, 1000)

    return () => clearInterval(interval)
  }, [verified])

  // Check verification (NO redirect)
  useEffect(() => {
    const interval = setInterval(async () => {
      const currentUser = auth.currentUser
      if (!currentUser || verified) return

      await currentUser.reload()

      if (currentUser.emailVerified) {
        localStorage.removeItem("verifyEmailStartTime")
        setVerified(true)
        showAlert(
          "Email verified successfully! Please refresh the page.",
          "success"
        )
        clearInterval(interval)
      }
    }, 2000)

    return () => clearInterval(interval)
  }, [verified])

  const handleResend = async () => {
    const currentUser = auth.currentUser
    if (!currentUser) {
      showAlert("No user found. Please login again.", "error")
      return
    }

    setResendLoading(true)
    try {
      await sendEmailVerification(currentUser)
      localStorage.setItem("verifyEmailStartTime", Date.now().toString())
      setSecondsLeft(50)
      showAlert("Verification email sent successfully!", "success")
    } catch {
      showAlert("Failed to send verification email.", "error")
    } finally {
      setResendLoading(false)
    }
  }

  const handleAbort = async () => {
    setAbortLoading(true)
    try {
      localStorage.removeItem("verifyEmailStartTime")
      await signOut(auth)
      navigate("/signup", { replace: true })
    } catch {
      showAlert("Failed to abort session.", "error")
      setAbortLoading(false)
    }
  }

  return (
    <div className={`login-wrapper ${loaded ? "loaded" : ""}`}>
      <div className="login-container">
        <div className="login-right">
          {!imgLoaded && <div className="image-skeleton"></div>}
          <img
            src="/assets/login2.png"
            alt="illustration"
            className={`login-image ${imgLoaded ? "show" : "hide"}`}
            onLoad={() => setImgLoaded(true)}
          />
        </div>

        <div className="login-left">
          <div className="login-content">
            <h1>Verify Your Email</h1>

            <p className="verify-email-text">
              We sent a verification link to
            </p>

            <div className="verify-email-address">
              {userEmail}
            </div>

            <p className="verify-email-text">
              Please verify to continue.
            </p>

            <div className="info-panel">
              <div className="info-icon">ℹ</div>
              <div className="info-content">
                <strong>Didn't receive the email?</strong>
                <ul>
                  <li>Check your <strong>spam/junk folder</strong></li>
                  <li>New platforms can be flagged as suspicious</li>
                </ul>
              </div>
            </div>

            {alert && (
              <div className={`alert ${alert.type}`}>
                <span className="alert-icon">
                  {alert.type === "success"
                    ? "✓"
                    : alert.type === "error"
                    ? "✕"
                    : "ℹ"}
                </span>
                <span>{alert.message}</span>
                <button className="alert-close" onClick={clearAlert}>×</button>
              </div>
            )}

            {verified && (
              <p className="verify-success-message">
                Email verified. Please refresh this page.
              </p>
            )}

            {/* Hide timer and resend completely after verification */}
            {!verified && (
              <>
                {secondsLeft > 0 ? (
                  <p className="verify-timer">
                    Resend available in {secondsLeft}s
                  </p>
                ) : (
                  <button
                    className="login-btn"
                    onClick={handleResend}
                    disabled={resendLoading}
                  >
                    {resendLoading ? "Sending..." : "Resend Verification Email"}
                  </button>
                )}
              </>
            )}

            <button
              className="login-btn abort-btn"
              onClick={handleAbort}
              disabled={abortLoading}
            >
              {abortLoading ? "Aborting..." : "Abort Session"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VerifyEmail